/**
 * Tipsy Design System - Spacing Tokens
 *
 * Spacing scale for consistent layouts across the Tipsy Super App.
 * Based on a 4px base unit.
 */

const BASE_UNIT = 4;

export const spacing = {
  /** 0px */
  none: 0,
  /** 2px */
  '2xs': BASE_UNIT * 0.5,
  /** 4px */
  xs: BASE_UNIT,
  /** 8px */
  sm: BASE_UNIT * 2,
  /** 12px */
  md: BASE_UNIT * 3,
  /** 16px */
  base: BASE_UNIT * 4,
  /** 20px */
  lg: BASE_UNIT * 5,
  /** 24px */
  xl: BASE_UNIT * 6,
  /** 32px */
  '2xl': BASE_UNIT * 8,
  /** 40px */
  '3xl': BASE_UNIT * 10,
  /** 48px */
  '4xl': BASE_UNIT * 12,
  /** 64px */
  '5xl': BASE_UNIT * 16,
  /** 80px */
  '6xl': BASE_UNIT * 20,
  /** 96px */
  '7xl': BASE_UNIT * 24,
} as const;

/**
 * Common layout spacing presets
 */
export const layout = {
  /** Screen horizontal padding */
  screenPaddingHorizontal: spacing.base,
  /** Screen vertical padding */
  screenPaddingVertical: spacing.lg,
  /** Card padding */
  cardPadding: spacing.base,
  /** Section gap */
  sectionGap: spacing['2xl'],
  /** Item gap in lists */
  listItemGap: spacing.md,
  /** Gap between form fields */
  formFieldGap: spacing.base,
  /** Gap between inline elements */
  inlineGap: spacing.sm,
  /** Icon spacing from text */
  iconGap: spacing.sm,
  /** Maximum content width for responsive layouts */
  maxContentWidth: 430,
} as const;

/**
 * Border radius tokens
 */
export const borderRadius = {
  /** 0px */
  none: 0,
  /** 4px */
  sm: 4,
  /** 8px */
  md: 8,
  /** 12px */
  lg: 12,
  /** 16px */
  xl: 16,
  /** 24px */
  '2xl': 24,
  /** Full circle */
  full: 9999,
} as const;

/**
 * Border width tokens
 */
export const borderWidth = {
  /** 0px */
  none: 0,
  /** 1px - Default border */
  thin: 1,
  /** 2px - Emphasized border */
  medium: 2,
  /** 3px - Heavy border */
  thick: 3,
} as const;

/**
 * Shadow/elevation presets for iOS and Android
 */
export const shadows = {
  none: {
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  sm: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  md: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  lg: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  xl: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 12,
  },
} as const;

export type Spacing = typeof spacing;
export type Layout = typeof layout;
export type BorderRadius = typeof borderRadius;
export type BorderWidth = typeof borderWidth;
export type Shadows = typeof shadows;
